import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import type { ExternalUserFindInterface } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-find.dto';
import { ExternalUserService } from '@zaoblako/web/core/services/security/external-user.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { PhonePipe } from '@zaoblako/web/shared/pipes/phone.pipe';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';
import { BaseSingleSelectComponent } from '@zaoblako/web/shared/components/base-single-select/base-single-select.component';
import { Observable } from 'rxjs';
import { DropdownBaseQueryInterface } from '@zaoblako/shared/common/dtos/dropdown-base-query.dto';
import { DEFAULT_LIMIT, DEFAULT_OFFSET } from '@zaoblako/shared/common/dtos/pagination-query.dto';
import { PaginationFindInterface } from '@zaoblako/shared/common/dtos/pagination-find.dto';
import { EXTERNAL_USER_WIDGET_REGISTRY_SELECT } from '@zaoblako/shared/modules/security/dtos/external-user/external-user-registry-select.dto';

@Component({
  standalone: true,
  selector: 'crm-external-user-single-select',
  templateUrl: './external-user-single-select.component.html',
  imports: [CommonModule, SingleSelectComponent, PhonePipe, SpinnerComponent, SafeHtmlPipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ExternalUserSingleSelectComponent
  extends BaseSingleSelectComponent<ExternalUserFindInterface>
  implements OnInit, OnChanges {
  public availableModule = 'BASIS';

  @Input() public clientIds: number[];
  @Input() public contragentIds: number[];

  /**
   * When true, skip registry load until `clientIds` or `contragentIds` is set
   * (avoids a useless unfiltered fetch before the parent filter is ready).
   */
  @Input() public requireFilter = false;

  public constructor(private readonly externalUserService: ExternalUserService) {
    super();
  }

  public override ngOnInit(): void {
    if (this.requireFilter && !this.hasFilter()) {
      return;
    }
    super.ngOnInit();
  }

  public override ngOnChanges(changes: SimpleChanges): void {
    let shouldReloadRegistry = false;

    if (changes.clientIds || changes.contragentIds) {
      if (this.requireFilter && !this.hasFilter()) {
        this.entities = [];
        this.cdRef.markForCheck();
      } else {
        shouldReloadRegistry = this.shouldReloadRegistryForFilterIds(changes);
      }
    }

    super.ngOnChanges(changes);

    if (shouldReloadRegistry) {
      this.firstLoad();
    }
  }

  public findById(id: number): Observable<ExternalUserFindInterface> {
    return this.externalUserService.findById(id, { withDeleted: true });
  }

  public findWithPagination(
    queryParams: Partial<DropdownBaseQueryInterface>
  ): Observable<PaginationFindInterface<ExternalUserFindInterface>> {
    return this.externalUserService.findRegistryItems({
      ...queryParams,
      limit: queryParams?.limit ?? DEFAULT_LIMIT,
      offset: queryParams?.offset ?? DEFAULT_OFFSET,
      select: [...EXTERNAL_USER_WIDGET_REGISTRY_SELECT],
      clientIds: this.clientIds,
      contragentIds: this.contragentIds,
      withDeleted: !!queryParams.includeIds?.length
    });
  }

  public toExternalUser(user: any): ExternalUserFindInterface {
    return user as ExternalUserFindInterface;
  }

  private hasFilter(): boolean {
    return !!(this.clientIds?.length || this.contragentIds?.length);
  }

  private shouldReloadRegistryForFilterIds(changes: SimpleChanges): boolean {
    const checkIdsChange = (key: 'clientIds' | 'contragentIds'): boolean => {
      const change = changes[key];
      if (!change) {
        return false;
      }
      if (change.firstChange) {
        return this.requireFilter && this.hasFilter() && this.entities.length === 0;
      }
      return !this.areIdListsEqual(change.previousValue, change.currentValue);
    };

    return checkIdsChange('clientIds') || checkIdsChange('contragentIds');
  }

  private areIdListsEqual(previous: number[] | null | undefined, current: number[] | null | undefined): boolean {
    const previousIds = previous ?? [];
    const currentIds = current ?? [];
    if (previousIds.length !== currentIds.length) {
      return false;
    }
    return previousIds.every((id, index) => id === currentIds[index]);
  }
}
